import React, { useEffect, useState } from 'react';
import { Database, LoaderCircle, Users } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Switch } from '../../components/ui/Switch';
import { AiReferenceImagesField } from '../../components/shared/AiReferenceImagesField';
import { DEFAULT_LLM_MODEL_KEY } from '../../constants/llmModels';
import { emptyCreateForm, buildCreateFeatures } from './projectFormUtils';
import { isProductCreateForbiddenMessage, ProductCreateErrorMessage } from './ProductCreateBanner';
import { classNames } from '../../utils/classNames';

const CapabilityRow = ({ icon: Icon, title, hint, checked, disabled, onToggle, children }) => (
  <section
    className={classNames(
      'rounded-2xl border px-3 py-2.5 transition',
      checked
        ? 'border-neutral-300 bg-white dark:border-neutral-700 dark:bg-neutral-950'
        : 'border-neutral-200 bg-neutral-50/80 dark:border-neutral-800 dark:bg-neutral-950/40'
    )}
  >
    <div className="flex items-center gap-3">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onToggle(!checked)}
        className="flex min-w-0 flex-1 items-start gap-3 text-start disabled:cursor-not-allowed"
      >
        <span
          className={classNames(
            'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border',
            checked
              ? 'border-neutral-300 text-neutral-800 dark:border-neutral-600 dark:text-neutral-100'
              : 'border-neutral-200 text-neutral-500 dark:border-neutral-800 dark:text-neutral-400'
          )}
        >
          <Icon size={16} />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-text">{title}</span>
          <span className="mt-0.5 block text-xs leading-5 text-muted">{hint}</span>
        </span>
      </button>
      <Switch checked={checked} disabled={disabled} onChange={onToggle} aria-label={title} />
    </div>
    {children}
  </section>
);

export const ProjectModal = ({ open, initialProject, error, productLocked = false, onClose, onSave }) => {
  const { t } = useI18n();
  const [form, setForm] = useState(emptyCreateForm);
  const [referenceImages, setReferenceImages] = useState([]);
  const [referenceImagesError, setReferenceImagesError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (!open) {
      setSaving(false);
      setUploadProgress(null);
      setReferenceImages([]);
      setReferenceImagesError('');
      setNameError('');
      return;
    }

    if (initialProject) {
      setForm({
        ...emptyCreateForm,
        name: initialProject.name || '',
        description: initialProject.description || '',
        model: DEFAULT_LLM_MODEL_KEY,
        enableDatabase: Boolean(initialProject.enableDatabase),
        enableAuth: Boolean(initialProject.enableAuth),
        registrationEnabled: initialProject.registrationEnabled !== false
      });
      setReferenceImages(initialProject.referenceImages || []);
    } else {
      setForm({ ...emptyCreateForm, model: DEFAULT_LLM_MODEL_KEY });
      setReferenceImages([]);
    }
  }, [initialProject, open]);

  const handleClose = () => {
    if (saving) {
      return;
    }

    onClose();
  };

  const submitHandler = async (event) => {
    event.preventDefault();

    const trimmedName = form.name.trim();
    const trimmedDescription = form.description.trim();

    if (!trimmedName) {
      setNameError(t('projectNameRequired'));
      return;
    }

    setNameError('');

    if (saving || productLocked) {
      return;
    }

    setSaving(true);
    setUploadProgress(null);
    setReferenceImagesError('');

    try {
      await onSave(
        {
          name: trimmedName,
          description: trimmedDescription,
          visibility: 'your',
          model: DEFAULT_LLM_MODEL_KEY,
          features: buildCreateFeatures(form),
          referenceImages
        },
        {
          onUploadProgress: (current, total) => setUploadProgress({ current, total })
        }
      );
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeDisabled={saving}
      title={t('createProject')}
      overlayClassName="items-end p-0 sm:items-center sm:p-4"
      panelClassName="flex max-h-[min(94dvh,760px)] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl rounded-b-none border-b-0 p-0 sm:max-h-[min(90vh,760px)] sm:rounded-[1.75rem] sm:border-b"
      contentClassName="mb-0 shrink-0 px-3 pb-0 pt-2.5 sm:px-3 sm:pt-3"
      titleClassName="text-base font-bold sm:text-lg"
      closeButtonClassName="h-9 w-9 shrink-0 rounded-full bg-white/70 dark:bg-slate-950/40"
      leadingSlot={
        <div
          className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-border/90 sm:hidden"
          aria-hidden
        />
      }
    >
      <form className="flex min-h-0 flex-1 flex-col" onSubmit={submitHandler}>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-3 pb-4">
          <div className="space-y-1">
            <Input
              label={t('projectName')}
              value={form.name}
              disabled={saving}
              required
              maxLength={80}
              placeholder={t('projectNamePlaceholder')}
              className="py-2.5"
              onChange={(event) => {
                setForm({ ...form, name: event.target.value });
                if (nameError) {
                  setNameError('');
                }
              }}
            />
            {nameError ? (
              <p className="text-xs text-rose-600 dark:text-rose-300" role="alert">
                {nameError}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-text">{t('createProjectCapabilities')}</p>
            <CapabilityRow
              icon={Database}
              title={t('createNeedDatabase')}
              hint={t('createNeedDatabaseHint')}
              checked={Boolean(form.enableDatabase)}
              disabled={saving}
              onToggle={(checked) => setForm({ ...form, enableDatabase: checked })}
            />
            <CapabilityRow
              icon={Users}
              title={t('createNeedUsers')}
              hint={t('createNeedUsersHint')}
              checked={Boolean(form.enableAuth)}
              disabled={saving}
              onToggle={(checked) => setForm({ ...form, enableAuth: checked })}
            >
              {form.enableAuth ? (
                <div className="mt-2.5 flex items-center justify-between gap-3 border-t border-neutral-200 pt-2.5 dark:border-neutral-800">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text">{t('featuresRegistrationEnabled')}</p>
                    <p className="mt-0.5 text-xs leading-5 text-muted">{t('featuresRegistrationEnabledHint')}</p>
                  </div>
                  <Switch
                    checked={Boolean(form.registrationEnabled)}
                    disabled={saving}
                    onChange={(checked) => setForm({ ...form, registrationEnabled: checked })}
                    aria-label={t('featuresRegistrationEnabled')}
                  />
                </div>
              ) : null}
            </CapabilityRow>
          </div>

          <AiReferenceImagesField
            value={referenceImages}
            disabled={saving}
            error={referenceImagesError}
            onChange={(files) => {
              setReferenceImages(files);
              if (referenceImagesError) {
                setReferenceImagesError('');
              }
            }}
          />

          {error ? (
            <div
              className="rounded-xl border border-rose-200 bg-rose-50/80 px-3 py-2.5 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200"
              role="alert"
            >
              {isProductCreateForbiddenMessage(error) ? <ProductCreateErrorMessage /> : error}
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-border/70 px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={saving}
          >
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={saving || productLocked}>
            {saving ? (
              <>
                <LoaderCircle size={16} className="animate-spin" />
                {uploadProgress
                  ? t('uploadingReferenceImages', {
                      current: uploadProgress.current,
                      total: uploadProgress.total
                    })
                  : t('creatingProject')}
              </>
            ) : (
              t('create')
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
